/**
 * 0.44.55 - Google map element.
 *
 * One component for the three places a map shows: the form editor canvas
 * (not interactive, so the iframe cannot swallow the clicks that select
 * and drag the element), the admin Preview and the public page.
 *
 * The map is Google's keyless embed (maps.google.com/maps?...&output=embed):
 * no API key, no billing account, nothing to configure on the server. It
 * shows a pin at the coordinates when they are set, otherwise Google's
 * own match for the address.
 *
 * "Load on click" keeps Google out of the page until the visitor asks:
 * nothing is requested from Google (and no Google cookie is set) before
 * the Show map button is pressed.
 */
import { useState } from 'react';
import Button from './Button';
import BuiltinIcon from './BuiltinIcon';
import styles from './GoogleMap.module.css';

export const MAP_TYPES = [
  { value: 'roadmap', label: 'Map (default)', t: 'm' },
  { value: 'satellite', label: 'Satellite', t: 'k' },
  { value: 'hybrid', label: 'Satellite with labels', t: 'h' },
  { value: 'terrain', label: 'Terrain', t: 'p' },
];

export const MAP_ZOOMS = [
  { value: '4', label: '4 - Several countries' },
  { value: '7', label: '7 - Country' },
  { value: '10', label: '10 - Region' },
  { value: '12', label: '12 - City' },
  { value: '14', label: '14 - District' },
  { value: '16', label: '16 - Streets (default)' },
  { value: '17', label: '17 - Street close-up' },
  { value: '18', label: '18 - Buildings' },
  { value: '20', label: '20 - Closest' },
];

export const MAP_HEIGHTS = [200, 250, 300, 350, 400, 450, 500, 600];

/* Latitude and longitude as numbers, or null when either is missing or
 * out of range. Accepts a comma as the decimal mark ("40,1792"). */
export function parseCoords(lat, lng) {
  const num = (v) => {
    const s = String(v ?? '').trim().replace(',', '.');
    if (!s) return NaN;
    return Number(s);
  };
  const a = num(lat);
  const b = num(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
}

/* "40.1792, 44.4991" or "40.1792 44.4991" (what Google Maps copies when
 * you right-click a place) split into its two numbers. */
export function splitCoordPair(text) {
  const m = String(text || '').trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  return m ? { lat: m[1], lng: m[2] } : null;
}

/* 0.44.56 - What Google is asked to show. pinBy 'address' (default):
 * the building address, so Google finds the real place and its card shows
 * the place's address; the coordinates only when there is no address.
 * pinBy 'coordinates': the exact point (Google's card may then say it
 * could not load the place), the address when the coordinates are unset.
 * Address details (apartment, company) are never sent. */
const query = ({ address, latitude, longitude, pinBy = 'address' }) => {
  const c = parseCoords(latitude, longitude);
  const a = String(address || '').replace(/\s+/g, ' ').trim();
  const coords = c ? `${c.lat},${c.lng}` : '';
  return pinBy === 'coordinates' ? (coords || a) : (a || coords);
};

export function mapEmbedUrl({ address, latitude, longitude, pinBy, zoom, mapType, language }) {
  const q = query({ address, latitude, longitude, pinBy });
  if (!q) return '';
  const t = (MAP_TYPES.find(m => m.value === mapType) || MAP_TYPES[0]).t;
  const z = Math.min(21, Math.max(1, Number(zoom) || 16));
  const hl = language ? `&hl=${encodeURIComponent(language)}` : '';
  return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=${t}&z=${z}${hl}&ie=UTF8&iwloc=&output=embed`;
}

export function mapOpenUrl(p) {
  const q = query(p);
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
}

export function mapDirectionsUrl(p) {
  const q = query(p);
  return q ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}` : '';
}

export default function GoogleMap({
  address = '', addressDetails = '', latitude = '', longitude = '', pinBy = 'address',
  zoom = 16, mapType = 'roadmap',
  language = '', height = 400, heightMobile = 300, frame = 'border',
  title = '', showAddress = false, showAddressDetails = true, showDirections = false, directionsLabel = '',
  showOpen = false, openLabel = '', loadOnClick = false, loadLabel = '',
  loadNotice = '', interactive = true, placeholder = '',
}) {
  const [loaded, setLoaded] = useState(false);
  const p = { address, latitude, longitude, pinBy };
  const src = mapEmbedUrl({ ...p, zoom, mapType, language });
  const h = Number(height) || 400;
  const hm = Number(heightMobile) || h;
  const boxStyle = { '--map-h': `${h}px`, '--map-h-sm': `${hm}px` };
  const framed = frame !== 'none';
  const name = (title || '').trim() || (address ? `Map: ${address}` : 'Map');

  if (!src) {
    return (
      <div className={`${styles.box} ${styles.empty}`} style={boxStyle}>
        <span>{placeholder || 'Set an address or coordinates to show the map.'}</span>
      </div>
    );
  }

  const showFrame = !loadOnClick || loaded;
  const links = (showDirections || showOpen) && (
    <div className={styles.links}>
      {showDirections && (
        <a href={interactive ? mapDirectionsUrl(p) : undefined} target="_blank" rel="noopener noreferrer"
           className={styles.link}>
          {directionsLabel || 'Get directions'}
          <BuiltinIcon name="open_in_new" size={16} />
        </a>
      )}
      {showOpen && (
        <a href={interactive ? mapOpenUrl(p) : undefined} target="_blank" rel="noopener noreferrer"
           className={styles.link}>
          {openLabel || 'Open in Google Maps'}
          <BuiltinIcon name="open_in_new" size={16} />
        </a>
      )}
    </div>
  );

  return (
    <div className={styles.wrap}>
      <div className={`${styles.box} ${framed ? styles.framed : ''}`} style={boxStyle}>
        {showFrame ? (
          <iframe
            className={styles.frame}
            src={src}
            title={name}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            style={interactive ? undefined : { pointerEvents: 'none' }}
            tabIndex={interactive ? undefined : -1}
          />
        ) : (
          <div className={styles.consent}>
            <BuiltinIcon name="location" size={32} />
            <p className={styles.notice}>
              {loadNotice || 'The map is provided by Google. Google may set cookies when it loads.'}
            </p>
            <Button variant="secondary" onClick={() => setLoaded(true)}>
              {loadLabel || 'Show map'}
            </Button>
          </div>
        )}
      </div>
      {/* 0.44.57 - The address and its details each have their own
          switch; shown together, the details go on the line below. */}
      {((showAddress && address) || (showAddressDetails && addressDetails)) && (
        <p className={styles.address}>
          {showAddress ? address : ''}
          {showAddress && address && showAddressDetails && addressDetails ? '\n' : ''}
          {showAddressDetails ? addressDetails : ''}
        </p>
      )}
      {links}
    </div>
  );
}

/* The element's props for <GoogleMap>. `resolve` runs a text through the
 * page's {field} / {column:...} / =expression resolution (identity in the
 * editor); `pageLanguage` is used when the element follows the page. */
export function elementMapProps(e, { resolve = (s) => s, pageLanguage = '' } = {}) {
  const r = (v) => String(resolve(v == null ? '' : String(v)) ?? '');
  return {
    address: r(e.address),
    addressDetails: r(e.address_details),
    pinBy: e.pin_by === 'coordinates' ? 'coordinates' : 'address',
    latitude: r(e.latitude),
    longitude: r(e.longitude),
    zoom: Number(e.zoom) || 16,
    mapType: e.map_type || 'roadmap',
    language: e.language && e.language !== 'page' ? e.language : (pageLanguage || ''),
    height: Number(e.height) || 400,
    heightMobile: Number(e.height_mobile) || 300,
    frame: e.frame || 'border',
    title: r(e.title),
    showAddress: !!e.show_address,
    // 0.44.57 - Unset (maps made before the switch): shown, as before.
    showAddressDetails: e.show_address_details !== false,
    showDirections: !!e.show_directions,
    directionsLabel: r(e.directions_label),
    showOpen: !!e.show_open,
    openLabel: r(e.open_label),
    loadOnClick: !!e.load_on_click,
    loadLabel: r(e.load_label),
    loadNotice: r(e.load_notice),
  };
}
